<script setup>
import { ref, computed } from "vue";
import MIcon from "@/components/m/MIcon.vue";
import MStars from "@/components/m/MStars.vue";
import BtSourceMark from "@/components/bt/BtSourceMark.vue";

/* Swipe-left-to-reveal review card — ported from the design SwipeCard.
   `r` is a normalized review: { source, name, rating, age, title, body,
   emotion, topic, sent: 'neg'|'pos'|'neu', urgent } */
const props = defineProps({
  r: { type: Object, required: true },
  replied: { type: Boolean, default: false },
});
const emit = defineEmits(["reply", "open"]);

// Single revealed action (Reply) — 84px wide to match .btm-swipe-act.
const REVEAL = 84;
const TRIG = 46;
const dx = ref(0);
const open = ref(false);
const start = ref(null);
const moved = ref(false);

const begin = (x) => {
  start.value = x;
  moved.value = false;
};
const move = (x) => {
  if (start.value == null) return;
  let d = x - start.value + (open.value ? -REVEAL : 0);
  if (Math.abs(x - start.value) > 6) moved.value = true;
  d = Math.max(-REVEAL - 30, Math.min(0, d));
  dx.value = d;
};
const end = () => {
  if (start.value == null) return;
  start.value = null;
  if (dx.value < -TRIG) {
    open.value = true;
    dx.value = -REVEAL;
  } else {
    open.value = false;
    dx.value = 0;
  }
};
const reset = () => {
  open.value = false;
  dx.value = 0;
};
const onTap = () => {
  if (moved.value) return;
  if (open.value) reset();
  else emit("open");
};

const sentColor = computed(() =>
  props.r.sent === "neg"
    ? "var(--neg)"
    : props.r.sent === "pos"
      ? "var(--pos)"
      : "var(--neu)",
);
const fgStyle = computed(() => ({
  transform: dx.value ? `translateX(${dx.value}px)` : "none",
  transition:
    start.value == null ? "transform .28s cubic-bezier(.32,.72,0,1)" : "none",
}));
</script>

<template>
  <div class="btm-swipe" style="margin-bottom: 11px">
    <div class="btm-swipe-actions">
      <button
        type="button"
        class="btm-swipe-act reply"
        aria-label="Reply to review"
        @click="
          reset();
          emit('reply');
        "
      >
        <MIcon name="reply" :size="20" />Reply
      </button>
    </div>
    <div
      class="btm-swipe-fg"
      :style="fgStyle"
      @touchstart="begin($event.touches[0].clientX)"
      @touchmove="move($event.touches[0].clientX)"
      @touchend="end"
      @pointerdown="begin($event.clientX)"
      @pointermove="$event.buttons && move($event.clientX)"
      @pointerup="end"
      @click="onTap"
    >
      <div style="display: flex; gap: 11px">
        <BtSourceMark :source="r.source" :size="30" />
        <div style="flex: 1; min-width: 0">
          <div
            style="
              display: flex;
              align-items: center;
              gap: 7px;
              margin-bottom: 3px;
            "
          >
            <span style="font-weight: 700; font-size: 13px">{{ r.name }}</span>
            <MStars :value="r.rating" :size="11" />
            <span
              class="btm-muted"
              style="margin-left: auto; font-size: 11px"
              >{{ r.age }}</span
            >
          </div>
          <div
            v-if="r.title"
            style="font-weight: 700; font-size: 13px; margin-bottom: 3px"
          >
            {{ r.title }}
          </div>
          <div
            style="
              font-size: 12.5px;
              color: var(--text-2);
              line-height: 1.45;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            "
          >
            <template v-if="r.body">{{ r.body }}</template>
            <span v-else style="font-style: italic; opacity: 0.7"
              >Rating only — no written comment</span
            >
          </div>
          <div
            style="
              display: flex;
              align-items: center;
              gap: 7px;
              margin-top: 8px;
            "
          >
            <span
              v-if="r.emotion"
              style="
                display: inline-flex;
                align-items: center;
                gap: 5px;
                font-size: 11px;
                font-weight: 700;
              "
              :style="{ color: sentColor }"
            >
              <span
                style="width: 6px; height: 6px; border-radius: 50%"
                :style="{ background: sentColor }"
              />{{ r.emotion }}
            </span>
            <span v-if="r.topic" class="btm-muted" style="font-size: 11px"
              >· {{ r.topic }}</span
            >
            <span v-if="replied" class="btm-tag pos" style="margin-left: auto">
              <MIcon name="check" :size="10" />Replied
            </span>
            <span
              v-else-if="r.urgent"
              class="btm-tag neg"
              style="margin-left: auto"
            >
              <MIcon name="flame" :size="10" fill />Urgent
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
